import { Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui/select'
import { MessageSquare, Mail, Phone, Star } from 'lucide-react'
import { type Contact } from '@/data/mockOrdersData'
import { cn } from '@/lib/utils'

interface PreferredContactSelectorProps {
	contact: Contact
	onPreferredMethodChange: (method: string) => void
	className?: string
}

const getContactMethodIcon = (method: string) => {
	const iconClass = "h-3 w-3"
	
	switch (method) {
		case 'sms':
			return <MessageSquare className={iconClass} />
		case 'email':
			return <Mail className={iconClass} />
		case 'phone':
			return <Phone className={iconClass} />
		case 'facebook':
			return (
				<svg className={iconClass} viewBox="0 0 24 24" fill="currentColor">
					<path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
				</svg>
			)
		default:
			return <MessageSquare className={iconClass} />
	}
}

const getContactMethodLabel = (method: string, contact: Contact) => {
	switch (method) {
		case 'sms':
			return `SMS ${contact.phone_raw || ''}`
		case 'email':
			return contact.email || 'Email'
		case 'phone':
			return `Call ${contact.phone_raw || ''}`
		case 'facebook':
			return 'Facebook'
		default:
			return method
	}
}

const getAllContactMethods = (contact: Contact): Array<{method: string, available: boolean}> => {
	return [
		{ method: 'sms', available: !!contact.phone_raw },
		{ method: 'email', available: !!contact.email },
		{ method: 'phone', available: !!contact.phone_raw },
		{ method: 'facebook', available: !!contact.facebook_id },
	]
}

export function PreferredContactSelector({ contact, onPreferredMethodChange, className }: PreferredContactSelectorProps) {
	const allMethods = getAllContactMethods(contact)
	const currentPreferred = contact.preferred_contact_method

	return (
		<div className={cn("flex items-center gap-1 text-xs text-muted-foreground", className)}>
			<Star className="h-3 w-3 fill-primary text-primary" />
			<Select value={currentPreferred} onValueChange={onPreferredMethodChange}>
				<SelectTrigger className="h-auto p-0 border-none shadow-none text-xs bg-transparent hover:bg-muted/50 rounded px-1">
					<div className="flex items-center gap-1">
						{getContactMethodIcon(currentPreferred)}
						<span className="capitalize">{currentPreferred}</span>
					</div>
				</SelectTrigger>
				<SelectContent>
					{allMethods.map(({ method, available }) => (
						<SelectItem 
							key={method} 
							value={method}
							disabled={!available}
							className={cn(
								"flex items-center gap-2",
								!available && "opacity-50 cursor-not-allowed"
							)}
						>
							<div className="flex items-center gap-2">
								{getContactMethodIcon(method)}
								<span>{getContactMethodLabel(method, contact)}</span>
							</div>
						</SelectItem>
					))}
				</SelectContent>
			</Select>
		</div>
	)
}
